import React, {Component} from 'react';
import {Modal, ModalHeader, ModalBody} from "reactstrap";
import {FaIcon} from "../FaIcon";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {Spinner} from "../Spinner";
import {DATA_BY_BUDGET_TYPE} from "../../budgets";
import {DatetimeHelper} from "../../utils/DatetimeHelper";
import {LoadErrorState} from "../elements/LoadErrorState";
import { withTranslation } from 'react-i18next';

// El `error_message` que trae el `APIClient` no es apto para pantalla: puede ser el `detail` en
// inglés de DRF, un genérico que inventa el propio cliente, o su texto de red —hardcodeado en
// español y en voseo—. Se resuelve por código, como en `InviteUserModal`.
const ERROR_MESSAGE_KEYS = {
    NETWORK_ERROR: 'ad_account_budget.load_error_network',
};

export function errorMessageKeyFor(response) {
    const code = response && (response.error_code || response.status);
    return ERROR_MESSAGE_KEYS[code] || 'ad_account_budget.load_error_explanation';
}

class AdAccountBudgetWithDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            detailModalIsOpen: false,
            campaignsBudgetDetails: null,
            loadErrorKey: null
        };

        this.detailModalToggle = this.detailModalToggle.bind(this);
        this.retryLoad = this.retryLoad.bind(this);
    }

    detailModalToggle() {
        const newDetailModalIsOpen = !this.state.detailModalIsOpen;
        this.setState({
            detailModalIsOpen: !this.state.detailModalIsOpen
        });

        if (!this.state.detailModalIsOpen && this.state.campaignsBudgetDetails === null)
            this.updateCampaignsBudgetDetails(newDetailModalIsOpen);
    }

    retryLoad() {
        this.updateCampaignsBudgetDetails(true);
    }

    updateCampaignsBudgetDetails(newDetailModalIsOpen) {
        this.setState({loadErrorKey: null});
        new APIClient(API_URL + `agency/ad-accounts/${this.props.adAccountId}/budgets`).call('GET')
            .then((response) => {
                if (response.status === 'OK') {
                    this.setState({
                        campaignsBudgetDetails: response.data,
                        detailModalIsOpen: newDetailModalIsOpen
                    });
                } else {
                    // El error se muestra DENTRO del modal, con reintentar. Con el `alert()` el modal
                    // quedaba abierto girando el spinner para siempre detrás del cartel del
                    // navegador: una falla se veía igual que una cuenta que tarda.
                    this.setState({
                        loadErrorKey: errorMessageKeyFor(response),
                        detailModalIsOpen: newDetailModalIsOpen
                    });
                }
            });
    }

    renderShowDetailIcon() {
        const { t } = this.props;
        let modalContent;
        if (this.state.loadErrorKey !== null) {
            modalContent = <LoadErrorState title={t('ad_account_budget.load_error')}
                                           text={t(this.state.loadErrorKey)}
                                           retryLabel={t('ad_account_budget.load_error_retry')}
                                           onRetry={this.retryLoad}/>;
        } else if (this.state.campaignsBudgetDetails !== null) {
            modalContent = <React.Fragment>
                <div className="table-box mt-">
                <table id="campaignBudgetsList" className="table table-striped">
                    <thead>
                    <tr>
                        <th scope="col">{t('ad_account_budget.campaign')}</th>
                        <th scope="col">{t('ad_account_budget.budget_breakdown')}</th>
                        <th scope="col" className="text-right">{t('ad_account_budget.daily_budget')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {this.renderCampaignBudgetDetailsRows()}
                    </tbody>
                </table>
            </div><div className="field-footnote mt-2">{`${t('ad_account_budget.last_update')} ${this.props.campaignsLastUpdate}`}</div></React.Fragment>;
        } else {
            modalContent = <div className="box-loader">
                <Spinner color="positive"/>
            </div>;
        }

        return (
            <span>
                <button className="edit-button ad-account-budgets-trigger" onClick={() => {
                    this.detailsModal.props.toggle()
                }}>
                    <FaIcon iconName={"info-circle"}/>
                </button>
                <Modal isOpen={this.state.detailModalIsOpen}
                       toggle={this.detailModalToggle}
                       className={`ad-account-budgets-modal ${this.props.className || ''}`}
                       ref={instance => {
                           this.detailsModal = instance;
                       }}>
                    <ModalHeader toggle={this.detailModalToggle}>{t('ad_account_budget.modal_title')}</ModalHeader>
                    {/* El contenido va dentro de `ModalBody`, como en los otros modales con tabla del
                        repo: sin él la regla `.modal-body .modal-section { padding: 2em }` no aplica,
                        el aire lo ponía el `.box` de adentro con otra medida, y el título quedaba
                        14px corrido respecto de la tabla. */}
                    <ModalBody>
                        <div className="modal-section ad-account-budgets">
                            {modalContent}
                        </div>
                    </ModalBody>
                    {/*<ModalFooter></ModalFooter>*/}
                </Modal>
            </span>
        );
    }

    renderCampaignBudgetDetailsRows() {
        return this.state.campaignsBudgetDetails.map((campaignBudgetDetails) => (this.renderBudgetDetailsRow(campaignBudgetDetails)));
    }

    renderBudgetType(campaignBudgetDetails) {
        const { t } = this.props;
        const budgetDetail = campaignBudgetDetails.budget_detail;
        if (!(budgetDetail.type in DATA_BY_BUDGET_TYPE))
            return t('ad_account_budget.budget_detection_error');

        const typeData = DATA_BY_BUDGET_TYPE[budgetDetail.type];

        let campaignPeriod = null;
        if (typeData["has_period"]) {
            const period = this.formatPeriod(budgetDetail["from_date"], budgetDetail["to_date"]);
            if (period)
                campaignPeriod = <div className="budget-campaign-detail">{period}</div>;
        }

        let campaignTotal = null;
        if (budgetDetail["lifetime_budget"])
            campaignTotal = <div className="budget-campaign-detail">
                {t('ad_account_budget.total_budget')} {this.formatAmount(budgetDetail["lifetime_budget"])}
            </div>;

        return <div className="budget-cell">
            <div className="budget-type">{t(typeData["title_key"])}</div>
            {campaignPeriod}
            {campaignTotal}
            {typeData["has_adset_detail"] ? this.renderAdsetsBlock(budgetDetail) : null}
        </div>;
    }

    renderAdsetsBlock(budgetDetail) {
        const { t } = this.props;
        const activeAdsets = budgetDetail.adsets_budget_details || [];
        // Los grupos no activos se listan con su presupuesto configurado, pero NO suman al
        // presupuesto diario de la campaña (un grupo apagado no gasta). Sin esto la lista mostraba
        // "1 de 2" sin decir por qué faltaba el otro, y se leía como un dato ausente.
        // El `|| []` cubre las campañas cuyo presupuesto se guardó antes de que existiera la lista.
        const inactiveAdsets = budgetDetail.inactive_adsets_budget_details || [];

        // El título promete una lista de grupos: si no hay ninguno, el hueco mudo se lee como un
        // dato que falta — el mismo malentendido que este modal vino a resolver.
        if (!activeAdsets.length && !inactiveAdsets.length)
            return <div className="budget-empty">{t('ad_account_budget.no_adsets_with_budget')}</div>;

        return <div className="budget-adsets">
            {activeAdsets.length
                ? <ul className="budget-list">{activeAdsets.map((adset) => this.renderAdsetDetails(adset))}</ul>
                : null}
            {/* Los no activos van en su propio bloque, con el aviso UNA sola vez arriba. Colgado de
                cada renglón, el aviso alargaba nombres que ya son largos y quedaba escondido
                adentro de la lista, justo donde hay que leerlo para entender el total. */}
            {inactiveAdsets.length
                ? <div className="budget-inactive">
                    <div className="budget-inactive-title">{t('ad_account_budget.inactive_adsets_title')}</div>
                    <ul className="budget-list">{inactiveAdsets.map((adset) => this.renderAdsetDetails(adset))}</ul>
                </div>
                : null}
        </div>;
    }

    renderAdsetDetails(adset) {
        const { t } = this.props;

        // Importe y período en UN renglón: separados en tres viñetas anidadas, un grupo ocupaba
        // cuatro renglones y la fila entera dejaba de entrar en pantalla.
        const details = [];
        if (adset.daily_budget)
            details.push(t('ad_account_budget.amount_per_day', {amount: this.formatAmount(adset.daily_budget)}));
        if (adset.total_budget)
            details.push(t('ad_account_budget.amount_total', {amount: this.formatAmount(adset.total_budget)}));

        const period = this.formatPeriod(adset.from_date, adset.to_date);
        if (period)
            details.push(period);

        return <li key={adset.id}>
            <span className="budget-adset-name">{adset.name}</span>
            {details.length ? <span className="budget-adset-detail">{details.join(' · ')}</span> : null}
        </li>;
    }

    renderExcludedFromTotalNote(campaignBudgetDetails) {
        const { t } = this.props;
        const budgetDetail = campaignBudgetDetails.budget_detail;

        // La nota habla de la lista de grupos, así que no puede aparecer donde esa lista no se
        // dibuja: un tipo sin detalle por grupo (Meta con CBO, por ejemplo, cuando le toque el turno)
        // mostraría «no incluye $X de grupos no activos» sin ningún grupo debajo al que referirse.
        const typeData = DATA_BY_BUDGET_TYPE[budgetDetail.type];
        if (!typeData || !typeData["has_adset_detail"])
            return null;

        const inactiveAdsets = budgetDetail.inactive_adsets_budget_details || [];
        if (!inactiveAdsets.length)
            return null;

        // La pregunta que deja la fila es aritmética — «si abajo dice $200.000, ¿por qué el total
        // es $0?» — y se contesta al lado del total, que es donde nace. Que el aviso esté en la
        // lista de grupos no alcanza: ahí explica el grupo, no la resta.
        const excludedDailyBudget = inactiveAdsets.reduce(
            (total, adset) => total + (Number(adset.daily_budget) || 0), 0);

        // Un grupo no activo puede declarar presupuesto TOTAL en vez de diario, y el total de la
        // columna lo convierte a diario del lado del backend. Sumar acá sólo los diarios daría un
        // monto que SUBESTIMA lo excluido, y el que haga la resta —para lo que existe la nota— no le
        // va a cerrar: con un solo grupo así, se dice la exclusión sin número. El tipo
        // ADSETS_TOTAL_AND_DAILY_BUDGET_FOR_PERIOD existe justo para cuando conviven los dos.
        const someAdsetOnlyHasTotalBudget = inactiveAdsets.some(
            (adset) => !adset.daily_budget && adset.total_budget);

        const note = excludedDailyBudget > 0 && !someAdsetOnlyHasTotalBudget
            ? t('ad_account_budget.excludes_inactive', {amount: this.formatAmount(excludedDailyBudget)})
            : t('ad_account_budget.excludes_inactive_without_amount');

        return <div className="budget-total-note">{note}</div>;
    }

    formatAmount(amount) {
        return `$${Number(amount).toLocaleString('en-US', {maximumFractionDigits: 0})}`;
    }

    formatPeriod(fromDate, toDate) {
        const { t } = this.props;
        const from = this.formatBudgetDate(fromDate);
        const to = this.formatBudgetDate(toDate);

        // `escapeValue: false` NO es decorativo: i18next escapa los valores interpolados por defecto
        // (`src/i18n.js` no lo apaga) y su mapa incluye la barra, así que «16/09/2026» llegaba a la
        // pantalla como «16&#x2F;09&#x2F;2026» — la misma fecha ilegible que este cambio vino a
        // arreglar. Es seguro: React escapa igual el nodo de texto.
        if (from && to)
            return t('ad_account_budget.period_range', {from: from, to: to, interpolation: {escapeValue: false}});
        if (from)
            return t('ad_account_budget.period_from', {date: from, interpolation: {escapeValue: false}});

        return null;
    }

    formatBudgetDate(aDate) {
        if (!aDate)
            return null;

        // El Backend serializa la fecha de la campaña con guiones (`2026-08-31`) y la del grupo sin
        // ellos (`20260831`): el helper come las dos. La del grupo se imprimía cruda cuando venía
        // sola, y «Desde 20260916» se lee como un número, no como una fecha.
        // Y si llegara cualquier otra cosa, se muestra tal cual: un dato raro no puede voltear el
        // modal entero.
        try {
            return DatetimeHelper.YYYYMMDDToFriendlyStr(String(aDate));
        } catch (e) {
            return String(aDate);
        }
    }

    renderBudgetDetailsRow(campaignBudgetDetails) {
        const formattedDailyBudget = parseInt(campaignBudgetDetails.budget_detail.daily_budget, 10).toLocaleString('en-US');

        return (
            <tr key={campaignBudgetDetails.campaign_id}>
                <td className="budget-campaign-name">
                    {campaignBudgetDetails.campaign_name}
                </td>
                <td>
                    {this.renderBudgetType(campaignBudgetDetails)}
                </td>
                <td className="text-right">
                    <div className="budget-total">{`$ ${formattedDailyBudget}`}</div>
                    {this.renderExcludedFromTotalNote(campaignBudgetDetails)}
                </td>
            </tr>
        );
    }

    render() {
        const { t } = this.props;
        if (this.props.channelHasBudgets === false)
            return "-";

        if (this.props.hasAccess === false)
            return <div className="field-footnote-darker mt-2">{t('ad_account_budget.no_access')}</div>;

        if (this.props.hasInternalProblem === true)
            return <div className="field-footnote-darker mt-2">{t('ad_account_budget.account_disabled')}</div>;

        if (this.props.campaignsLastUpdate === null || this.props.dailyBudget === null)
            return <div className="field-footnote mt-2">{t('ad_account_budget.no_information')}</div>;


        const formattedDailyBudget = parseInt(this.props.dailyBudget, 10).toLocaleString('en-US');
        const formattedMaxCampaignDailyBudget =  parseInt(this.props.maxCampaignDailyBudget, 10).toLocaleString('en-US');

        return <span>{this.props.currency}$ {formattedDailyBudget} ({t('ad_account_budget.max_campaign')} {this.props.currency}$ {formattedMaxCampaignDailyBudget}){this.renderShowDetailIcon()}</span>
    }
}

export default withTranslation()(AdAccountBudgetWithDetails);
